// src/tabs/calendar/_liste-helpers.js — PR #169c (Vue Liste réservations)
//
// Helpers purs pour la vue "Liste" CalendarPanel (3e sous-onglet 📋 Liste).
//
// Source unifiée : reservations ∪ pricing_blocks "direct interne FBZ"
// (guest_first_name présent). Cohérent ADR-105 (kind discriminator) + ADR-106
// (agrégation unifiée Compta).
//
// Réutilise `flattenBlocksMap` de `_today-helpers.js` (anti-régression
// Pattern PR #169d NEW-1 : réinjection logement_id depuis clé Map).
//
// CONVENTION end_date (Convention A unifiée, cohérent _block-helpers.js /
// _today-helpers.js) :
//   - reservation ET pricing_block : EXCLUSIVE — end_date = jour de checkout
//   → getNightsCount = diffDays indépendamment de kind.
//
// Pas de side-effect, pas de fetch : helpers appelables inline OU mémoïsés.
//
// Pattern cohérent _today-helpers.js / _block-helpers.js.

import { flattenBlocksMap } from "./_today-helpers"

// ── Allowlists pour safeListeState (anti-LS-corruption M-1 amend v1.1) ───
//
// 9 colonnes triables (cohérent UI ListeView headers).
//
// Commit 5 fix régression : `booking_date` → `created_at` (colonne réelle DB,
// `booking_date` n'existe pas — smoke prod erreur 42703). `created_at` est
// stocké en YYYY-MM-DD dans rows[] (sliced depuis TIMESTAMPTZ) — voir
// buildUnifiedRows.
export const LISTE_SORT_KEYS = [
  "logement_id",
  "guest_first_name",
  "created_at",
  "start_date",
  "end_date",
  "nights",
  "guest_count",
  "source",
  "status",
]

// 4 plateformes filtrables (cohérent SOURCE_BG + Block direct interne FBZ).
export const LISTE_PLATFORMS = ["airbnb", "booking", "direct", "block"]

const DEFAULT_LISTE_STATE = {
  sortKey: "start_date",
  sortDir: "desc",
  filterPlatforms: LISTE_PLATFORMS.slice(),
}

/**
 * Helper jours entre start (inclusive) et end (exclusive).
 * Convention A unifiée (cohérent _block-helpers.js + _today-helpers.js) :
 *   - reservation ET pricing_block : end EXCLUSIVE → nights = (end - start)
 *
 * @param {string} startISO YYYY-MM-DD
 * @param {string} endISO YYYY-MM-DD (exclusive)
 * @param {'reservation'|'pricing_block'} [kind] conservé pour rétro-compat
 *   d'appel ; n'a plus d'effet sur le calcul (Convention A unifiée).
 * @returns {number} nuits (>=0)
 */
export function getNightsCount(startISO, endISO, kind) {
  if (!startISO || !endISO) return 0
  const [ys, ms, ds] = startISO.split("-").map(Number)
  const [ye, me, de] = endISO.split("-").map(Number)
  const start = Date.UTC(ys, ms - 1, ds)
  const end = Date.UTC(ye, me - 1, de)
  const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24))
  return diffDays
}

/**
 * Construit rows unifiées : résas + blocks "direct interne FBZ".
 *
 * Réutilise `flattenBlocksMap` (export `_today-helpers.js`) pour la réinjection
 * `logement_id` depuis la clé Map (anti-régression Pattern PR #169d NEW-1).
 *
 * Filtre les blocks sans `guest_first_name` (cohérent ADR-105/106) : blocks
 * maintenance/blocage simple non inclus dans la Liste (ils ne représentent
 * pas une "résa direct interne FBZ").
 *
 * Schéma row unifié :
 *   {
 *     id, kind, logement_id,
 *     guest_first_name, guest_last_name, guest_email,
 *     source, status, start_date, end_date, created_at (YYYY-MM-DD),
 *     guest_count, total_amount, nights,
 *     raw (référence brute, pour modal kind-aware)
 *   }
 *
 * Note `created_at` : pour `reservations`, raw `created_at` est TIMESTAMPTZ
 * ISO complet (`2026-04-15T12:00:00Z`). On le slice à YYYY-MM-DD pour
 * cohérence affichage + tri (équivalent ex-`booking_date` DATE).
 * Idem pricing_blocks.
 *
 * source 'block' attribué aux pricing_blocks pour cohérence filtre
 * `LISTE_PLATFORMS` (vs source réelle 'direct' qui ferait conflit avec résas
 * direct CSV). NB : kind discriminator reste source de vérité ADR-105.
 *
 * @param {Array<object>} reservations rows reservations brutes Supabase
 * @param {Map<string, Array<object>>|null|undefined} blocksByLogement
 * @returns {Array<object>} rows unifiées
 */
export function buildUnifiedRows(reservations, blocksByLogement) {
  const rows = []

  // 1. Reservations (toutes, cancelled inclus — filtré via filterStatut UI)
  //    `created_at` (TIMESTAMPTZ) sliced à YYYY-MM-DD pour cohérence affichage
  //    et tri (équivalent ex-`booking_date` DATE).
  for (const r of reservations || []) {
    rows.push({
      id: r.id,
      kind: "reservation",
      logement_id: r.logement_id,
      guest_first_name: r.guest_first_name ?? null,
      guest_last_name: r.guest_last_name ?? null,
      guest_email: r.guest_email ?? null,
      source: r.source ?? null,
      status: r.status ?? null,
      start_date: r.start_date,
      end_date: r.end_date,
      created_at: typeof r.created_at === "string" ? r.created_at.slice(0, 10) : null,
      guest_count: r.guest_count ?? null,
      total_amount: r.total_amount ?? null,
      nights: getNightsCount(r.start_date, r.end_date, "reservation"),
      raw: r,
    })
  }

  // 2. Blocks direct interne FBZ (guest_first_name présent) — réinjection
  //    logement_id depuis clé Map via flattenBlocksMap (réutilisé _today-helpers).
  const allBlocks = flattenBlocksMap(blocksByLogement)
  for (const b of allBlocks) {
    const firstName = b.guest_first_name
    const hasGuest =
      typeof firstName === "string" && firstName.trim() !== ""
    if (!hasGuest) continue
    rows.push({
      id: b.id,
      kind: "pricing_block",
      logement_id: b.logement_id,
      guest_first_name: b.guest_first_name ?? null,
      guest_last_name: b.guest_last_name ?? null,
      guest_email: b.guest_email ?? null,
      source: "block", // catégorie UI filtre (vs raw.source possiblement 'direct')
      status: null,
      start_date: b.start_date,
      end_date: b.end_date,
      created_at: typeof b.created_at === "string" ? b.created_at.slice(0, 10) : null,
      guest_count: b.guest_count ?? null,
      total_amount: b.total_amount ?? null,
      nights: getNightsCount(b.start_date, b.end_date, "pricing_block"),
      raw: b,
    })
  }

  return rows
}

/**
 * Applique filtres composites sur les rows unifiées.
 *
 * Filtres :
 *   - searchQuery (string) : matche guest_first_name OR guest_last_name OR
 *     guest_email (case-insensitive).
 *   - filterLogement (string|'all') : exact match logement_id.
 *   - filterStatut (object|null) : si fourni, applique callback `match(row)`.
 *     CalendarPanel passe une fonction extraite (réutilisation directe
 *     `applyStatutFilter` interne).
 *   - filterPlatforms (Set<string>|Array<string>) : appartenance row.source.
 *
 * @param {Array<object>} rows
 * @param {{
 *   searchQuery?: string,
 *   filterLogement?: string,
 *   filterStatutMatch?: (row: object) => boolean,
 *   filterPlatforms?: Set<string>|Array<string>,
 * }} filters
 * @returns {Array<object>}
 */
export function filterRows(rows, filters = {}) {
  const {
    searchQuery = "",
    filterLogement = "all",
    filterStatutMatch = null,
    filterPlatforms = null,
  } = filters

  let out = rows || []

  if (filterLogement && filterLogement !== "all") {
    out = out.filter((r) => r.logement_id === filterLogement)
  }

  if (filterPlatforms) {
    const set =
      filterPlatforms instanceof Set
        ? filterPlatforms
        : new Set(filterPlatforms)
    // Si toutes les plateformes connues sont cochées → no-op (perf).
    if (set.size < LISTE_PLATFORMS.length) {
      out = out.filter((r) => set.has(r.source))
    }
  }

  const q = (searchQuery || "").trim().toLowerCase()
  if (q) {
    out = out.filter((r) => {
      const hay = `${r.guest_first_name || ""} ${r.guest_last_name || ""} ${r.guest_email || ""}`
        .toLowerCase()
      return hay.includes(q)
    })
  }

  if (typeof filterStatutMatch === "function") {
    out = out.filter((r) => {
      // pricing_blocks : statut métier ne s'applique pas (pas de payment_received_at,
      // etc.). On les inclut par défaut sauf si le filter explicitement matche.
      // CalendarPanel passera filterStatutMatch=null pour 'all' → cette branche skip.
      try {
        return filterStatutMatch(r)
      } catch (_) {
        return true
      }
    })
  }

  return out
}

/**
 * sortRows — tri ASC/DESC mono-colonne.
 *
 * Convention null-last : valeurs null/undefined toujours en fin (indépendant
 * de sortDir). Cas légitime : created_at null si malformé (defensive),
 * guest_first_name null sur blocks maintenance (exclus par buildUnifiedRows
 * mais defensive ici).
 *
 * sortKey supporté : cf. LISTE_SORT_KEYS.
 *
 * @param {Array<object>} rows
 * @param {string} sortKey
 * @param {'asc'|'desc'} sortDir
 * @returns {Array<object>} nouvelle array triée (n'altère pas l'input)
 */
export function sortRows(rows, sortKey, sortDir = "desc") {
  if (!Array.isArray(rows) || rows.length === 0) return rows || []
  const safeKey = LISTE_SORT_KEYS.includes(sortKey) ? sortKey : "start_date"
  const dir = sortDir === "asc" ? 1 : -1
  const copy = rows.slice()
  copy.sort((a, b) => {
    const va = a?.[safeKey]
    const vb = b?.[safeKey]
    const aNull = va === null || va === undefined || va === ""
    const bNull = vb === null || vb === undefined || vb === ""
    // Null-last (indépendant dir)
    if (aNull && bNull) return 0
    if (aNull) return 1
    if (bNull) return -1
    // Tri natif : nombres OK, strings localeCompare
    if (typeof va === "number" && typeof vb === "number") {
      return (va - vb) * dir
    }
    return String(va).localeCompare(String(vb)) * dir
  })
  return copy
}

/**
 * safeListeState — sanitize state localStorage `fbz-liste-state`.
 *
 * Protège contre LS corrompu (sortKey='foobar' → crash colonne header
 * introuvable → écran blanc). Allowlist stricte sortKey/sortDir/filterPlatforms.
 *
 * @param {string|null} raw JSON string (ou null si LS vide)
 * @returns {{sortKey: string, sortDir: 'asc'|'desc', filterPlatforms: string[]}}
 */
export function safeListeState(raw) {
  try {
    const p = JSON.parse(raw || "{}")
    return {
      sortKey: LISTE_SORT_KEYS.includes(p.sortKey) ? p.sortKey : DEFAULT_LISTE_STATE.sortKey,
      sortDir: ["asc", "desc"].includes(p.sortDir) ? p.sortDir : DEFAULT_LISTE_STATE.sortDir,
      filterPlatforms: Array.isArray(p.filterPlatforms)
        ? p.filterPlatforms.filter((x) => LISTE_PLATFORMS.includes(x))
        : DEFAULT_LISTE_STATE.filterPlatforms.slice(),
    }
  } catch (_) {
    return {
      sortKey: DEFAULT_LISTE_STATE.sortKey,
      sortDir: DEFAULT_LISTE_STATE.sortDir,
      filterPlatforms: DEFAULT_LISTE_STATE.filterPlatforms.slice(),
    }
  }
}
